import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { isRealAthleteId } from './useSetLogs'

export interface AthleteAccess {
  access_status: 'active' | 'paused'
  billing_required: boolean
  subscription_status: string | null
  assignmentId: string | null
  startedAt: string | null
  weeksUnlocked: number | null
  currentWeek: number | null
  totalWeeks: number
  autoWeek: number
  unlockedWeek: number
}

function computeAuto(startedAt: string | null): number {
  if (!startedAt) return 1
  const days = Math.floor((Date.now() - new Date(startedAt).getTime()) / 86400000)
  return Math.floor(days / 7) + 1
}

/** Coach view of an athlete's access + week-unlock state. */
export function useAthleteAccess(athleteId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['athlete-access', athleteId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId),
    queryFn: async (): Promise<AthleteAccess> => {
      const { data: ath, error: e1 } = await supabase
        .from('athletes')
        .select('access_status, billing_required, subscription_status')
        .eq('id', athleteId)
        .single()
      if (e1) throw e1
      const { data: ap } = await supabase
        .from('athlete_programs')
        .select('id, started_at, weeks_unlocked, current_week, program:programs(template)')
        .eq('athlete_id', athleteId)
        .eq('active', true)
        .maybeSingle()

      const total =
        (ap?.program as { template?: { weeks?: unknown[] } } | null)?.template?.weeks?.length ?? 0
      const auto = Math.min(Math.max(total, 1), computeAuto(ap?.started_at ?? null))
      const unlocked = Math.min(
        Math.max(total, 1),
        Math.max(1, ap?.weeks_unlocked ?? auto)
      )
      return {
        access_status: (ath.access_status as 'active' | 'paused') ?? 'active',
        billing_required: Boolean(ath.billing_required),
        subscription_status: (ath.subscription_status as string | null) ?? null,
        assignmentId: ap?.id ?? null,
        startedAt: ap?.started_at ?? null,
        weeksUnlocked: ap?.weeks_unlocked ?? null,
        currentWeek: ap?.current_week ?? null,
        totalWeeks: total,
        autoWeek: auto,
        unlockedWeek: unlocked,
      }
    },
  })
}

/** Coach mutations: toggle access, pin/advance/hold the unlocked week, resume auto. */
export function useAccessMutations(athleteId: string) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['athlete-access', athleteId] })

  const setAccess = useMutation({
    mutationFn: async (status: 'active' | 'paused') => {
      // Record that the COACH paused them, so a Stripe event can't silently undo it.
      const { error } = await supabase
        .from('athletes')
        .update({ access_status: status, access_paused_by: status === 'paused' ? 'coach' : null })
        .eq('id', athleteId)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const setUnlockedWeek = useMutation({
    mutationFn: async ({ assignmentId, week }: { assignmentId: string; week: number | null }) => {
      const { error } = await supabase
        .from('athlete_programs')
        .update({ weeks_unlocked: week })
        .eq('id', assignmentId)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const setBilling = useMutation({
    mutationFn: async (required: boolean) => {
      const { error } = await supabase.from('athletes').update({ billing_required: required }).eq('id', athleteId)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { setAccess, setUnlockedWeek, setBilling }
}
